import { v4 as uuidv4 } from "uuid";
import { ScopeTag } from "@rebuild/shared";
import type { CreateRateItemInput, RateItem } from "./rateCards.types";

export function createEmptyRateItem(): CreateRateItemInput {
  return {
    unit: "",
    unitPrice: 0,
    lineItemTemplate: {
      id: uuidv4(),
      originalLineItem: "",
      category: "",
      elements: [{ type: "text", value: "" }],
      scopes: [ScopeTag.time_and_material],
    },
  };
}

/**
 * Returns a new CreateRateItemInput with the description updated.
 * Also keeps `lineItemTemplate.elements[0].value` in sync — the server derives
 * the inline template from `originalLineItem`, so we mirror edits here.
 */
export function setItemDescription(item: CreateRateItemInput, description: string): CreateRateItemInput {
  const existingElements = item.lineItemTemplate.elements ?? [];
  const firstElement = existingElements[0] ?? { type: "text", value: "" };
  const nextElements = [{ ...firstElement, value: description }, ...existingElements.slice(1)];
  return {
    ...item,
    lineItemTemplate: {
      ...item.lineItemTemplate,
      originalLineItem: description,
      elements: nextElements,
    },
  };
}

export function setItemCategory(item: CreateRateItemInput, category: string): CreateRateItemInput {
  return {
    ...item,
    lineItemTemplate: {
      ...item.lineItemTemplate,
      category,
    },
  };
}

export function setItemUnit(item: CreateRateItemInput, unit: string): CreateRateItemInput {
  return { ...item, unit };
}

export function setItemUnitPrice(item: CreateRateItemInput, unitPrice: number): CreateRateItemInput {
  return { ...item, unitPrice };
}

/**
 * Maps a persisted RateItem to the editable CreateRateItemInput shape. The top-level `id`
 * is preserved so the detail modal can tell existing items (PATCH) apart from new ones (POST).
 */
export function rateItemToInput(item: RateItem): CreateRateItemInput {
  return {
    id: item.id,
    unit: item.unit,
    unitPrice: item.unitPrice,
    lineItemTemplate: item.lineItemTemplate,
  };
}

/** True if the draft differs from the persisted item and therefore needs to be saved. */
export function hasRateItemChanged(draft: CreateRateItemInput, original: RateItem): boolean {
  return (
    draft.unit !== original.unit ||
    draft.unitPrice !== original.unitPrice ||
    JSON.stringify(draft.lineItemTemplate) !== JSON.stringify(original.lineItemTemplate)
  );
}

export function isValidRateItem(item: CreateRateItemInput): boolean {
  const hasDescription = item.lineItemTemplate.originalLineItem.trim().length > 0;
  const hasUnit = item.unit.trim().length > 0;
  const hasValidPrice = Number.isFinite(item.unitPrice) && item.unitPrice >= 0;
  return hasDescription && hasUnit && hasValidPrice;
}
